'use client';

import classNames from 'classnames';
import { type ChangeEvent, forwardRef, type InputHTMLAttributes } from 'react';

import { Spinner } from '../spinner/spinner';

import styles from './checkbox-input.module.scss';

type CheckboxInputProps = InputHTMLAttributes<HTMLInputElement> & {
  dataTestId?: string;
  id?: string;
  isChecked: boolean;
  isLoading?: boolean;
  isSwitch?: boolean;
  label: string;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  pushToRight?: boolean;
  wrapperClassName?: string;
};

export const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    {
      dataTestId,
      id,
      isChecked,
      isLoading = false,
      isSwitch = false,
      label,
      name,
      onChange,
      pushToRight,
      wrapperClassName,
      ...restProps
    },
    ref
  ) => {
    const fieldId = id ?? name;

    const renderSwitch = () => {
      const control = (
        <input
          {...restProps}
          checked={Boolean(isChecked)}
          className={classNames({
            [styles['checkbox__control']]: !isSwitch,
            [styles['switch__control']]: isSwitch,
          })}
          id={fieldId}
          onChange={onChange}
          ref={ref}
          type="checkbox"
          {...(isSwitch ? { role: 'switch' } : {})}
        />
      );

      if (!isLoading) return control;

      return (
        <span className={styles['checkbox-input__loader']}>
          <Spinner />
          {control}
        </span>
      );
    };

    return (
      <div
        className={classNames(
          {
            [styles['checkbox']]: !isSwitch,
            [styles['switch']]: isSwitch,
          },
          wrapperClassName
        )}
        data-testid={dataTestId ?? fieldId}
      >
        <label
          className={classNames({
            [styles['checkbox__label']]: !isSwitch,
            [styles['switch__label']]: isSwitch,
          })}
          htmlFor={fieldId}
        >
          {!pushToRight && renderSwitch()}
          <span>{label}</span>
          {pushToRight && renderSwitch()}
        </label>
      </div>
    );
  }
);
